import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Target, Zap, TrendingUp, Clock, DollarSign, Brain, 
  ChevronRight, ArrowRight, CheckCircle2, Trophy, Calendar,
  Dumbbell, Heart, Timer, Route
} from 'lucide-react';
import { Button } from '../components/ui/button';

const events = [
  { name: 'Hyrox', icon: '🏋️', desc: 'Functional fitness racing' },
  { name: 'CrossFit', icon: '💪', desc: 'High-intensity varied training' },
  { name: '5K Run', icon: '🏃', desc: 'Speed & endurance focus' },
  { name: '10K Run', icon: '🏃‍♂️', desc: 'Aerobic conditioning' },
  { name: 'Half Marathon', icon: '🎽', desc: '21.1km distance prep' },
  { name: 'Marathon', icon: '🏅', desc: '42.2km ultimate endurance' },
  { name: 'Ultra Marathon', icon: '⛰️', desc: 'Beyond the distance' },
  { name: 'Tough Mudder', icon: '🧗', desc: 'Obstacle course mastery' },
  { name: 'Spartan Race', icon: '🔥', desc: 'Strength meets grit' },
  { name: 'True Grit', icon: '💥', desc: 'Australian OCR challenge' },
  { name: 'Bodybuilding', icon: '🏆', desc: 'Physique competition prep' },
];

const benefits = [
  {
    icon: Target,
    title: 'Laser-Focused Training',
    desc: 'Every session designed specifically for your event demands. No wasted effort, no generic programs.'
  },
  {
    icon: Clock,
    title: 'Rapid Plan Generation',
    desc: 'Get your complete periodized program in minutes, not weeks. AI analyzes thousands of event-specific protocols instantly.'
  },
  {
    icon: DollarSign,
    title: 'Fraction of the Cost',
    desc: 'Event-specific coaches charge $200-500/month. Get the same expertise for less than a coffee per day.'
  },
  {
    icon: Brain,
    title: 'State-of-the-Art Methods',
    desc: 'HIIT, tempo runs, lactate threshold training, sport-specific drills - all scientifically programmed for peak performance.'
  },
  {
    icon: TrendingUp,
    title: 'Progressive Periodization',
    desc: 'Your plan adapts week-by-week, building toward peak performance on race day.'
  },
  {
    icon: Heart,
    title: 'Nutrition That Matches',
    desc: 'Event-specific fueling strategies for training, tapering, and race day execution.'
  },
];

export default function EventTraining() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
              <span className="text-black font-black text-xl">V</span>
            </div>
            <span className="font-bold text-xl tracking-tight">VYTA</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/about" className="text-zinc-400 hover:text-white transition-colors">About</Link>
            <Link to="/science" className="text-zinc-400 hover:text-white transition-colors">Science</Link>
            <Link to="/login" className="text-zinc-400 hover:text-white transition-colors">Login</Link>
            <Button onClick={() => navigate('/register')} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-6">
              Start Training
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#CCFF00]/5 to-transparent" />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#CCFF00]/10 border border-[#CCFF00]/20 mb-6">
              <Trophy className="w-4 h-4 text-[#CCFF00]" />
              <span className="text-sm font-medium text-[#CCFF00]">Event-Specific Training</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight">
              Train Specifically.
              <br />
              <span className="text-[#CCFF00]">Perform Exceptionally.</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-zinc-400 mb-8 max-w-2xl mx-auto">
              Generic training produces generic results. Get an AI-powered program built specifically 
              for YOUR event—whether it's a Hyrox, marathon, Spartan Race, or bodybuilding competition.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/register')} 
                size="lg"
                className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-8 h-14 text-lg"
              >
                Build My Event Plan <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-black mb-4">Choose Your Battle</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Select your event and we'll create a program optimized for exactly what you'll face on race day.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {events.map((event, i) => (
              <motion.div
                key={event.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 hover:border-[#CCFF00]/50 transition-all cursor-pointer group"
                onClick={() => navigate('/register')}
              >
                <div className="text-4xl mb-3">{event.icon}</div>
                <h3 className="font-bold text-lg mb-1 group-hover:text-[#CCFF00] transition-colors">{event.name}</h3>
                <p className="text-sm text-zinc-500">{event.desc}</p>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: events.length * 0.05 }}
              className="p-6 rounded-2xl bg-gradient-to-br from-[#CCFF00]/10 to-transparent border border-[#CCFF00]/30 hover:border-[#CCFF00] transition-all cursor-pointer group"
              onClick={() => navigate('/register')}
            >
              <div className="text-4xl mb-3">✨</div>
              <h3 className="font-bold text-lg mb-1 text-[#CCFF00]">Custom Event</h3>
              <p className="text-sm text-zinc-400">Any event. We'll build it.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Event-Specific */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-black mb-4">Why Event-Specific Training?</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              A marathon runner doesn't train like a CrossFitter. A Hyrox athlete doesn't eat like a bodybuilder. 
              Your event has unique demands—your training should too.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-zinc-900/30 border border-zinc-800"
              >
                <div className="w-12 h-12 rounded-xl bg-[#CCFF00]/10 flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-[#CCFF00]" />
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-zinc-400">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-black mb-4">The VYTA Advantage</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Traditional */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl bg-zinc-900/50 border border-zinc-800"
            >
              <h3 className="text-xl font-bold mb-6 text-zinc-400">Traditional Event Coaching</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-zinc-500">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>$200-500/month for event-specific coach</span>
                </li>
                <li className="flex items-start gap-3 text-zinc-500">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>Weeks to create a personalized program</span>
                </li>
                <li className="flex items-start gap-3 text-zinc-500">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>Limited by coach's event experience</span>
                </li>
                <li className="flex items-start gap-3 text-zinc-500">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>Nutrition plan sold separately</span>
                </li>
                <li className="flex items-start gap-3 text-zinc-500">
                  <span className="text-red-500 mt-1">✗</span>
                  <span>Slow adjustments when life happens</span>
                </li>
              </ul>
            </motion.div>

            {/* VYTA */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl bg-gradient-to-br from-[#CCFF00]/10 to-transparent border border-[#CCFF00]/30"
            >
              <h3 className="text-xl font-bold mb-6 text-[#CCFF00]">VYTA Event Training</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#CCFF00] mt-0.5 flex-shrink-0" />
                  <span>Less than $1/day for complete event preparation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#CCFF00] mt-0.5 flex-shrink-0" />
                  <span>Full periodized program generated in minutes</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#CCFF00] mt-0.5 flex-shrink-0" />
                  <span>AI trained on latest event-specific research</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#CCFF00] mt-0.5 flex-shrink-0" />
                  <span>Training + nutrition integrated as one system</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#CCFF00] mt-0.5 flex-shrink-0" />
                  <span>Instant program updates when needed</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-black mb-4">What's In Your Event Plan?</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Calendar, title: 'Periodized Schedule', desc: 'Week-by-week progression leading to peak performance on event day' },
              { icon: Dumbbell, title: 'Sport-Specific Workouts', desc: 'HIIT, tempo, threshold, strength—all tailored to your event' },
              { icon: Route, title: 'Race Strategy', desc: 'Pacing, nutrition timing, and mental preparation tactics' },
              { icon: Timer, title: 'Taper Protocol', desc: 'Scientifically optimized rest period before your event' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-6"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#CCFF00]/10 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-[#CCFF00]" />
                </div>
                <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-zinc-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-black mb-4">
              Your Event. <span className="text-[#CCFF00]">Your Plan.</span> Your Best Performance.
            </h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              Stop training generically for a specific event. Get a program designed for exactly what you're training for.
            </p>
            <Button 
              onClick={() => navigate('/register')} 
              size="lg"
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold rounded-full px-8 h-14 text-lg"
            >
              Start My Event Training <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-sm text-zinc-500 mt-4">7-day free trial • Cancel anytime</p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-900">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#CCFF00] flex items-center justify-center">
              <span className="text-black font-black">V</span>
            </div>
            <span className="font-bold">VYTA</span>
          </div>
          <p className="text-sm text-zinc-500">© 2026 VYTA. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
